import {
  Box,
  Button,
  type DefaultOptionType,
  ErrorMessage,
  Flex,
  Modal,
  Select,
  Space,
  Tag,
  Text,
} from '@/components';
import { VITE_ENV_NAME } from '@/config';
import { t } from '@/i18n';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { type SelectProps, theme } from 'antd';
import type { FormItemLayout } from 'antd/es/form/Form';
import { type CSSProperties, type ReactNode, useState } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';

type ListHeightType = 'small' | 'medium';
const ListHeightTypeMapper = {
  small: 256,
  medium: 512,
} as const satisfies Record<ListHeightType, number>;

type FilterOptionModal = {
  active: boolean;
  modalTitle?: string;
  onConfirm: () => void;
  onClear: () => void;
  component: ReactNode;
};

type FilterOptionSettingField = {
  key: string;
  active: boolean;
  component: ReactNode;
};

export type SelectFieldProps<T extends FieldValues> = ({
  name: Path<T>;
  label?: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  showSearch?: boolean;
  disabled?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  hint?: string;
  width?: CSSProperties['width'];
  filterOptionModal?: FilterOptionModal;
  filterOptionSettingField?: FilterOptionSettingField;
  wrapperStyle?: CSSProperties;
  shouldCoexistOnChange?: boolean;
  tagMaxWidth?: number | string;
  existenceCheckOptions?: SelectProps['options'];
  labelSize?: FieldLabelSize;
  listHeight?: ListHeightType;
  // TODO:  it would be better if we can use this for mode: undefined too.
  //        ref: https://github.com/ant-design/ant-design/issues/23374
  useNotInOption?: boolean;
  textNotInOption?: string;
  caption?: ReactNode;
  onChange?: (value: any, options: DefaultOptionType[]) => void;
  labelStyle?: CSSProperties;
  layout?: FormItemLayout;
} & Pick<
  SelectProps,
  | 'options'
  | 'searchValue'
  | 'onSearch'
  | 'optionRender'
  | 'filterOption'
  | 'notFoundContent'
  | 'labelRender'
  | 'tagRender'
  | 'onDropdownVisibleChange'
  | 'tokenSeparators'
  | 'onInputKeyDown'
  | 'dropdownRender'
>) &
  (
    | { mode?: undefined }
    | {
        mode: 'multiple' | 'tags';
        autoClearSearchValue?: boolean;
      }
  );

const GAP = 8;

export const SelectField = <T extends FieldValues>({
  name,
  label,
  control,
  options,
  errors,
  required,
  showSearch: _showSearch,
  disabled,
  loading,
  allowClear: _allowClear,
  placeholder,
  hint,
  shouldCoexistOnChange = false,
  width = '100%',
  filterOption,
  filterOptionModal,
  filterOptionSettingField,
  wrapperStyle,
  tagMaxWidth = 180,
  existenceCheckOptions,
  labelSize = 'medium',
  listHeight = 'small',
  searchValue,
  onChange,
  onSearch,
  optionRender,
  labelRender,
  tagRender,
  onDropdownVisibleChange,
  notFoundContent,
  tokenSeparators,
  onInputKeyDown,
  caption,
  dropdownRender,
  labelStyle,
  layout,
  ...rest
}: SelectFieldProps<T>) => {
  const { token } = theme.useToken();

  const [openModal, setOpenModal] = useState(false);
  const [openModalButtonRef, setOpenModalButtonRef] =
    useState<HTMLElement | null>(null);
  const [showSettingFieldButtonRef, setShowSettingFieldButtonRef] =
    useState<HTMLElement | null>(null);
  const [showSettingField, setShowSettingField] = useState(false);
  const allowClear = _allowClear ?? !required;

  /**
   * @description showSearch is true when mode is multiple.
   * @link https://ant.design/components/select/#components-select-demo-multiple
   */
  const showSearch = _showSearch ?? rest.mode === 'multiple';

  return (
    <>
      <Form.Item
        label={
          label && (
            <FieldLabel
              text={label}
              required={required}
              hint={hint}
              size={labelSize}
              style={labelStyle}
            />
          )
        }
        layout={layout}
        style={wrapperStyle}
      >
        <Flex vertical>
          {caption && (
            <Box marginTop={-4} marginBottom={8}>
              {typeof caption === 'string' ? (
                <Text
                  text={caption}
                  color="secondary"
                  weight="regular"
                  style={{ fontSize: 12 }}
                />
              ) : (
                caption
              )}
            </Box>
          )}
          <Flex>
            <Controller
              control={control}
              name={name}
              render={({ field }) => {
                const { onChange: fieldOnChange, value, ...restField } = field;

                const getValue = () => {
                  if (loading) {
                    return null;
                  }
                  if (rest.mode || !rest.useNotInOption || !value) {
                    return value;
                  }
                  const exist = (existenceCheckOptions ?? options)?.some(
                    (option) => option.value === value,
                  );

                  return exist
                    ? value
                    : (rest.textNotInOption ?? t('not-exist-in-option'));
                };

                return (
                  <Space.Compact
                    style={{
                      width: showSettingFieldButtonRef
                        ? `calc(100% - ${showSettingFieldButtonRef.offsetWidth + GAP}px)`
                        : width,
                    }}
                    block
                  >
                    <Select
                      {...restField}
                      virtual={VITE_ENV_NAME !== 'mock'}
                      aria-label={label}
                      value={getValue() || []}
                      mode={rest.mode}
                      options={options}
                      optionRender={optionRender}
                      filterOption={
                        (filterOption ?? showSearch)
                          ? (input, options) =>
                              options && typeof options.label === 'string'
                                ? options.label.includes(input)
                                : true
                          : false
                      }
                      showSearch={showSearch}
                      searchValue={searchValue}
                      onSearch={onSearch}
                      listHeight={ListHeightTypeMapper[listHeight]}
                      tokenSeparators={tokenSeparators}
                      disabled={disabled}
                      allowClear={allowClear}
                      placeholder={placeholder}
                      notFoundContent={notFoundContent}
                      dropdownRender={dropdownRender}
                      onDropdownVisibleChange={onDropdownVisibleChange}
                      onChange={(
                        value,
                        _option:
                          | DefaultOptionType
                          | DefaultOptionType[]
                          | undefined,
                      ) => {
                        if (shouldCoexistOnChange || !onChange) {
                          // NOTE: If allowClear is true, value can be null but type of value is not nullable.
                          fieldOnChange(value ?? null);
                        }

                        const options = (
                          Array.isArray(_option)
                            ? _option
                            : _option === undefined
                              ? []
                              : [_option]
                        ).filter((option) => option.value);
                        onChange?.(value, options);
                      }}
                      onInputKeyDown={onInputKeyDown}
                      labelRender={labelRender}
                      style={
                        openModalButtonRef
                          ? {
                              width: `calc(100% - ${openModalButtonRef.offsetWidth}px)`,
                            }
                          : undefined
                      }
                      {...(rest.mode && {
                        autoClearSearchValue: rest.autoClearSearchValue ?? true,
                      })}
                      {...(rest.mode && {
                        tagRender: (props) => {
                          const { label, isMaxTag, ...restProps } = props;
                          if (tagRender) {
                            return tagRender(props);
                          }
                          const textStyle = {
                            maxWidth: tagMaxWidth,
                            ellipsis: true,
                          };
                          const exist = (
                            existenceCheckOptions ?? options
                          )?.some((option) => option.value === props.value);
                          if (!value || loading) {
                            // To avoid type error in tagRender
                            return <></>;
                          }
                          if (!exist) {
                            return (
                              <Tag {...restProps}>
                                <Text
                                  text={
                                    !rest.useNotInOption &&
                                    (typeof label === 'string' ||
                                      typeof label === 'number')
                                      ? label
                                      : (rest.textNotInOption ??
                                        t('not-exist-in-option'))
                                  }
                                  {...textStyle}
                                />
                              </Tag>
                            );
                          }
                          return (
                            <Tag {...restProps}>
                              {typeof label === 'string' ? (
                                <Text text={label} {...textStyle} />
                              ) : (
                                label
                              )}
                            </Tag>
                          );
                        },
                      })}
                    />
                    {filterOptionModal && (
                      <Flex>
                        <Button
                          ref={(ref) => {
                            setOpenModalButtonRef(ref);
                          }}
                          style={{ height: '100%' }}
                          onClick={() => {
                            setOpenModal(true);
                          }}
                          disabled={disabled}
                        >
                          {filterOptionModal.active ? (
                            <FilterFilled />
                          ) : (
                            <FilterOutlined />
                          )}
                        </Button>
                      </Flex>
                    )}
                  </Space.Compact>
                );
              }}
            />
            {filterOptionSettingField && (
              <Flex>
                <Button
                  ref={(ref) => {
                    setShowSettingFieldButtonRef(ref);
                  }}
                  style={{
                    height: '100%',
                    marginLeft: GAP,
                  }}
                  onClick={() => {
                    setShowSettingField((prev) => !prev);
                  }}
                  disabled={disabled}
                >
                  {filterOptionSettingField.active ? (
                    <FilterFilled />
                  ) : (
                    <FilterOutlined />
                  )}
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>

        {filterOptionSettingField && showSettingField && (
          <Box
            key={filterOptionSettingField.key}
            background={token.colorBgLayout}
            marginTop={8}
          >
            {filterOptionSettingField.component}
          </Box>
        )}

        <ErrorMessage errors={errors} name={name} />
      </Form.Item>
      {filterOptionModal && (
        <Modal
          title={filterOptionModal.modalTitle}
          open={openModal}
          okText={t('apply-filter')}
          onOk={() => {
            filterOptionModal.onConfirm();
            setOpenModal(false);
          }}
          cancelText={t('clear')}
          onCancel={() => {
            filterOptionModal.onClear();
            setOpenModal(false);
          }}
          closable={false}
          maskClosable={false}
        >
          {filterOptionModal.component}
        </Modal>
      )}
    </>
  );
};
